/**
 * Event Bus for inter-module communication
 * Provides centralized event system for loose coupling between modules
 */

import { logError } from './error-handler.js';

class EventBus {
    constructor() {
        this.events = new Map();
    }

    /**
     * Emit an event with optional data
     * @param {string} eventName - Name of the event
     * @param {*} data - Data to pass with the event
     */
    emit(eventName, data = null) {
        // Dispatch custom event on window for global listeners
        window.dispatchEvent(new CustomEvent(eventName, { detail: data }));

        // Also handle local event listeners
        const listeners = this.events.get(eventName);
        if (listeners) {
            listeners.forEach(listener => {
                try {
                    listener(data);
                } catch (error) {
                    logError(error, {
                        eventName,
                        type: 'event_listener',
                        listenerFunction: listener.name || 'anonymous'
                    });
                }
            });
        }
    }

    /**
     * Subscribe to an event
     * @param {string} eventName - Name of the event
     * @param {Function} callback - Callback function
     * @returns {Function} Unsubscribe function
     */
    on(eventName, callback) {
        if (!this.events.has(eventName)) {
            this.events.set(eventName, new Set());
        }
        this.events.get(eventName).add(callback);

        // Return unsubscribe function
        return () => this.off(eventName, callback);
    }

    /**
     * Unsubscribe from an event
     * @param {string} eventName - Name of the event
     * @param {Function} callback - Callback function to remove
     */
    off(eventName, callback) {
        const listeners = this.events.get(eventName);
        if (listeners) {
            listeners.delete(callback);
            if (listeners.size === 0) {
                this.events.delete(eventName);
            }
        }
    }

    /**
     * Subscribe to an event that will only fire once
     * @param {string} eventName - Name of the event
     * @param {Function} callback - Callback function
     * @returns {Function} Unsubscribe function
     */
    once(eventName, callback) {
        const onceCallback = data => {
            callback(data);
            this.off(eventName, onceCallback);
        };
        return this.on(eventName, onceCallback);
    }

    /**
     * Remove all listeners for an event or all events
     * @param {string} [eventName] - Optional event name, if not provided removes all
     */
    removeAllListeners(eventName) {
        if (eventName) {
            this.events.delete(eventName);
        } else {
            this.events.clear();
        }
    }

    /**
     * Get list of all registered event names
     * @returns {string[]}
     */
    getEventNames() {
        return Array.from(this.events.keys());
    }

    /**
     * Get number of listeners for an event
     * @param {string} eventName - Name of the event
     * @returns {number}
     */
    getListenerCount(eventName) {
        const listeners = this.events.get(eventName);
        return listeners ? listeners.size : 0;
    }
}

// Create singleton instance
const eventBus = new EventBus();

// Export both the class and the singleton instance
export { EventBus };
export default eventBus;

// Common event names as constants
export const EVENTS = {
    // Filter system events
    FILTER_CHANGED: 'filter:changed',
    FILTER_CLEARED: 'filter:cleared',
    FILTER_APPLIED: 'filter:applied',
    FILTER_LOADED: 'filter:loaded',
    FILTER_MANAGER_READY: 'filter:managerReady',
    FILTER_MANAGER_ERROR: 'filter:managerError',

    // Grid events
    GRID_REFRESHED: 'grid:refreshed',
    GRID_DATA_LOADED: 'grid:dataLoaded',
    GRID_ERROR: 'grid:error',
    GRID_TOGGLE_SUCCESS: 'grid:toggleSuccess',
    GRID_DELETE_SUCCESS: 'grid:deleteSuccess',
    GRID_SELECTION_CHANGED: 'grid:selectionChanged',

    // GridManager events
    GRID_MANAGER_READY: 'gridManager:ready',
    GRID_MANAGER_ERROR: 'gridManager:error',
    GRID_MANAGER_DESTROYED: 'gridManager:destroyed',

    // UI events
    SIDEBAR_TOGGLED: 'ui:sidebarToggled',
    MODAL_OPENED: 'ui:modalOpened',
    MODAL_CLOSED: 'ui:modalClosed',

    // Application events
    APP_READY: 'app:ready',
    APP_ERROR: 'app:error',
    USER_LOGGED_OUT: 'user:loggedOut',
    SESSION_EXPIRED: 'session:expired',

    // AJAX events
    AJAX_START: 'ajax:start',
    AJAX_SUCCESS: 'ajax:success',
    AJAX_ERROR: 'ajax:error',
    AJAX_COMPLETE: 'ajax:complete'
};
